/* OPS Gift Wrap extension for the shared OPS Product Maker.
 * Reuses its artwork state, My Artwork bridge, upload Worker and verified cart flow.
 */
(function(){
  'use strict';
  const SELECTOR='[data-ops-maker][data-preset="gift-wrap-tiling"]';
  const LENGTHS=[500,600,700,800,900,1000,1100,1200,1300,1400,1500,1600,1700,1800,1900,2000];
  const clamp=(n,a,b)=>Math.min(b,Math.max(a,n));
  const priceForLength=mm=>4.95+((clamp(Math.round(Number(mm)/100)*100,500,2000)-500)/100*0.20);
  const priceLabel=minor=>new Intl.NumberFormat('en-GB',{style:'currency',currency:'GBP'}).format((Number(minor)||0)/100);
  const sheetPixelDimensions=(lengthMm,dpi=300,widthMm=305)=>({width:Math.round(widthMm/25.4*dpi),height:Math.round(lengthMm/25.4*dpi)});
  const variantForLength=(variants,lengthMm,optionIndex=0)=>(Array.isArray(variants)?variants:[]).find(v=>{
    const label=`${v?.options?.[optionIndex]||''} ${v?.title||''}`,match=label.match(/(\d{3,4})\s*mm/i)||String(v?.sku||'').match(/OPS-GW-(\d{4})/i);
    return Number(match?.[1])===Number(lengthMm);
  })||null;
  const cartPropertiesFor=details=>({'Sheet dimensions':`${details.widthMm} × ${details.lengthMm} mm`,'_Gift wrap variant ID':String(details.variantId||''),'_Gift wrap variant title':String(details.variantTitle||''),'_Gift wrap quantity':String(details.quantity||1),'_Gift wrap pattern':JSON.stringify(details.pattern||{})});
  const esc=s=>String(s).replace(/[&<>"']/g,c=>({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  const isMirror=mode=>/^mirror-/.test(mode);

  function getLayout(mode,cell,gap,offsetX,offsetY){
    let cols=1,rows=1;
    if(mode==='mirror-2')cols=2;
    if(mode==='mirror-4'){cols=2;rows=2}
    if(mode==='mirror-8'){cols=4;rows=2}
    const groupW=cols*cell+(cols-1)*gap,groupH=rows*cell+(rows-1)*gap;
    return {cols,rows,groupW,groupH,mode,cell,gap,offsetX,offsetY};
  }

  function paintGroup(ctx,tile,x,y,layout,sequence=[0],sequenceStart=0){
    const tiles=Array.isArray(tile)?tile:[tile],order=Array.isArray(sequence)&&sequence.length?sequence:[0];
    for(let row=0;row<layout.rows;row++)for(let col=0;col<layout.cols;col++){
      const mirrorX=isMirror(layout.mode)&&(col%2===1);
      const mirrorY=isMirror(layout.mode)&&(row%2===1);
      const dx=x+col*(layout.cell+layout.gap),dy=y+row*(layout.cell+layout.gap);
      const sequenceIndex=((sequenceStart+row*layout.cols+col)%order.length+order.length)%order.length;
      const source=tiles[order[sequenceIndex]]||tiles[0];
      if(!source)continue;
      ctx.save();
      if(mirrorX||mirrorY){
        ctx.translate(dx+(mirrorX?layout.cell:0),dy+(mirrorY?layout.cell:0));
        ctx.scale(mirrorX?-1:1,mirrorY?-1:1);
        ctx.drawImage(source,0,0,layout.cell,layout.cell);
      }else ctx.drawImage(source,dx,dy,layout.cell,layout.cell);
      ctx.restore();
    }
  }

  function drawPattern(ctx,tile,width,height,opts){
    const mode=opts.mode||'repeat',cell=Math.max(1,opts.cellPx|0),gap=Math.max(0,opts.gapPx|0);
    const layout=getLayout(mode,cell,gap,opts.offsetXPx|0,opts.offsetYPx|0);
    const order=Array.isArray(opts.sequence)&&opts.sequence.length?opts.sequence:[0];
    ctx.fillStyle=opts.background||'#ffffff';ctx.fillRect(0,0,width,height);
    const stepX=layout.groupW+gap,stepY=layout.groupH+gap;
    const rows=Math.ceil(height/Math.max(1,stepY))+3,cols=Math.ceil(width/Math.max(1,stepX))+3;
    for(let row=-1;row<rows;row++)for(let col=-1;col<cols;col++){
      let x=col*stepX+(opts.offsetXPx||0),y=row*stepY+(opts.offsetYPx||0);
      if(mode==='stagger'&&Math.abs(row%2)===1)x+=stepX/2;
      if(mode==='half-drop'&&Math.abs(col%2)===1)y+=stepY/2;
      if(x+layout.groupW<0||y+layout.groupH<0||x>width||y>height)continue;
      const sequenceStart=((col%order.length)+order.length)%order.length;
      paintGroup(ctx,tile,x,y,layout,order,sequenceStart);
    }
    return layout;
  }

  function initialize(root){
    const maker=root.opsMakerInstance;if(!maker||root.dataset.giftWrapEnhanced)return;
    root.dataset.giftWrapEnhanced='1';root.classList.add('ops-maker--gift-wrap');
    const stageWrap=root.querySelector('.ops-maker__stage-wrap'),stage=root.querySelector('.ops-maker__stage'),panel=root.querySelector('.ops-maker__panel'),order=root.querySelector('.ops-maker__order');
    if(!stageWrap||!stage||!panel||!order)return;
    const MAX_TILES=8;maker.state=maker.state.slice(0,1);maker.surface=0;
    let tileSequence=[0],sheetBackground='#ffffff';

    const controls=document.createElement('section');controls.className='ops-gift-wrap__controls ops-maker__group';controls.innerHTML=`
      <h3>Pattern</h3>
      <label class="ops-maker__field"><span>Arrangement</span><select data-gw-mode>
        <option value="repeat">Repeat</option><option value="stagger">Stagger</option><option value="half-drop">Half-drop</option>
        <option value="mirror-2">2-way mirror</option><option value="mirror-4">4-way mirror</option><option value="mirror-8">8-way mirror</option>
      </select></label>
      <label class="ops-maker__field"><span>Repeat tile size <output data-gw-cell-readout>200 mm</output></span><input data-gw-cell type="range" min="100" max="305" step="5" value="200"></label>
      <label class="ops-maker__field"><span>Gap <output data-gw-gap-readout>0 mm</output></span><input data-gw-gap type="range" min="0" max="40" step="1" value="0"></label>
      <div class="ops-maker__row"><label class="ops-maker__field"><span>Pattern offset X</span><input data-gw-x type="range" min="-100" max="100" step="1" value="0"></label><label class="ops-maker__field"><span>Pattern offset Y</span><input data-gw-y type="range" min="-100" max="100" step="1" value="0"></label></div>
      <p class="ops-gift-wrap__warning" data-gw-warning hidden>Mirroring can reverse text and logos. Check every detail in the sheet proof.</p>
      <p class="ops-gift-wrap__resolution" data-gw-resolution aria-live="polite"></p>`;
    const artGroup=panel.querySelector('[data-maker-panel="artwork"]');
    if(artGroup){
      const uploadButton=artGroup.querySelector('[data-upload-button]');
      if(uploadButton){
        const savedButton=document.createElement('button');
        savedButton.type='button';
        savedButton.className='ops-maker__button';
        savedButton.textContent='Choose from My Artwork';
        savedButton.dataset.gwChooseSaved='';
        uploadButton.after(savedButton);
        savedButton.addEventListener('click',()=>{
          if(typeof window.OPSArtworkSource?.open==='function')window.OPSArtworkSource.open(maker.file);
          else maker.status('My Artwork is unavailable. Use Upload artwork instead.','error');
        });
      }
      artGroup.after(controls);
    }else panel.append(controls);
    const tileTools=document.createElement('section');tileTools.className='ops-gift-wrap__tile-tools';tileTools.innerHTML=`
      <h3>Tile designs</h3>
      <p class="ops-gift-wrap__help">Build a repeat from separate designs. Each tile can hold several artworks and text blocks.</p>
      <a class="ops-gift-wrap__studio-link" href="/pages/pattern-studio"><span><strong>Make a tiling pattern in Pattern Studio</strong><small>For fabric, tablecloths and other surfaces</small></span><b aria-hidden="true">→</b></a>
      <div class="ops-gift-wrap__tile-selector" data-gw-tiles role="group" aria-label="Choose a tile to edit"></div>
      <button type="button" class="ops-maker__button" data-gw-add-tile>+ Add tile</button>
      <div class="ops-gift-wrap__sequence">
        <strong>Repeat order</strong>
        <p class="ops-gift-wrap__help">This order repeats across every row, for example 1, 2, 3 → 1, 2, 3.</p>
        <div class="ops-gift-wrap__sequence-list" data-gw-sequence></div>
        <button type="button" class="ops-maker__button ops-maker__button--small" data-gw-append-tile>Add selected tile to order</button>
        <button type="button" class="ops-maker__button ops-maker__button--small" data-gw-reset-sequence>Reset order</button>
      </div>
      <label class="ops-maker__field"><span>Sheet background</span><input type="color" value="#ffffff" data-gw-background aria-label="Sheet background colour"></label>
      <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" multiple data-gw-extra-upload hidden>
      <div class="ops-maker__actions"><button type="button" class="ops-maker__button ops-maker__button--primary" data-gw-extra-upload-button>+ Upload artwork to this tile</button><button type="button" class="ops-maker__button" data-gw-extra-library>Choose from My Artwork</button></div>
      <p class="ops-gift-wrap__help">Select an artwork on the tile to move, resize or remove it. Add text as often as you need.</p>
      <div class="ops-gift-wrap__art-controls" data-gw-art-controls hidden>
        <label class="ops-maker__field"><span>Selected artwork size</span><input type="range" min="0.15" max="3" step="0.01" value="1" data-gw-art-scale></label>
        <button type="button" class="ops-maker__button ops-maker__button--danger" data-gw-delete-art>Remove selected artwork</button>
      </div>`;
    controls.append(tileTools);
    const originalTabs=maker.qa('[data-surface]');originalTabs.forEach(button=>button.hidden=true);
    const tileSelector=tileTools.querySelector('[data-gw-tiles]'),sequenceList=tileTools.querySelector('[data-gw-sequence]');
    const renderTileControls=()=>{
      maker.preset.surfaces=maker.state.map((_,index)=>`Tile ${index+1}`);
      maker.qa('[data-surface]').forEach(button=>button.hidden=true);
      tileSelector.innerHTML=maker.state.map((_,index)=>`<button type="button" class="ops-gift-wrap__tile-button${maker.surface===index?' is-active':''}" data-gw-tile="${index}" aria-pressed="${maker.surface===index}" aria-label="Edit tile ${index+1}">Tile ${index+1}</button>`).join('');
      sequenceList.innerHTML=tileSequence.map((tile,index)=>`<button type="button" class="ops-gift-wrap__sequence-chip" data-gw-remove-sequence="${index}" aria-label="Remove tile ${tile+1} from repeat order">${tile+1} <span aria-hidden="true">×</span></button>`).join('');
      tileTools.querySelector('[data-gw-append-tile]').textContent=`Add tile ${maker.surface+1} to order`;
    };
    const activateTile=index=>{
      if(index<0||index>=maker.state.length)return;
      maker.surface=index;maker.writeControls();renderTileControls();maker.draw();
    };

    const views=document.createElement('div');views.className='ops-gift-wrap__views';
    stage.parentNode.insertBefore(views,stage);views.append(stage);
    const full=document.createElement('section');full.className='ops-gift-wrap__sheet-panel';full.innerHTML=`<header><div><h3>Your wrapping paper</h3><p data-gw-dimensions>305 mm × 0.5 m</p></div><button type="button" class="ops-maker__button ops-maker__button--small" data-gw-open-sheet-preview>Enlarge preview</button></header><div class="ops-gift-wrap__sheet-viewport"><canvas data-gw-sheet aria-label="Repeated wrapping paper preview"></canvas></div>`;
    views.append(full);
    const canvas=root.querySelector('[data-gw-sheet]'),ctx=canvas.getContext('2d');
    const toolbar=stageWrap.querySelector('.ops-maker__stage-toolbar');
    if(toolbar){const previewButton=document.createElement('button');previewButton.type='button';previewButton.className='ops-maker__button ops-maker__button--small';previewButton.dataset.gwOpenTilePreview='';previewButton.textContent='Enlarge tile preview';toolbar.append(previewButton);previewButton.addEventListener('click',()=>openPreview('tile'))}
    const previewDialog=document.createElement('dialog');previewDialog.className='ops-gift-wrap__preview-dialog';previewDialog.innerHTML=`
      <div class="ops-gift-wrap__preview-header"><strong data-gw-preview-title>Preview</strong><button type="button" class="ops-maker__button ops-maker__button--small" data-gw-close-preview>Close</button></div>
      <div class="ops-gift-wrap__preview-viewport"><canvas data-gw-preview-canvas></canvas></div>`;
    root.append(previewDialog);
    const previewCanvas=previewDialog.querySelector('[data-gw-preview-canvas]');previewCanvas.setAttribute('aria-label','Expanded design preview');
    const openPreview=async kind=>{
      try{
        const preview=kind==='sheet'?await drawFullSheet({production:false}):await maker._opsGiftWrapBaseRender(maker.surface,{preview:true});
        previewCanvas.width=preview.width;previewCanvas.height=preview.height;
        previewCanvas.getContext('2d').clearRect(0,0,previewCanvas.width,previewCanvas.height);
        previewCanvas.getContext('2d').drawImage(preview,0,0);
        previewDialog.querySelector('[data-gw-preview-title]').textContent=kind==='sheet'?'Full sheet preview':`Tile ${maker.surface+1} preview`;
        if(!previewDialog.open)previewDialog.showModal();
      }catch(error){maker.status('The preview could not be opened. Please try again.','error');console.error(error)}
    };
    previewDialog.addEventListener('click',event=>{if(event.target===previewDialog)previewDialog.close()});
    previewDialog.addEventListener('close',()=>{});


    const productOption=()=>maker.selectedOptionEntries().find(x=>/^length$/i.test(x.name));
    const parseLength=value=>Number(String(value||'').match(/(500|600|700|800|900|1000|1100|1200|1300|1400|1500|1600|1700|1800|1900|2000)\s*mm/i)?.[1])||null;
    const optionLength=()=>parseLength(productOption()?.value||maker.variant?.title);
    let selectedLengthMm=optionLength()||500;
    const lengthFromVariant=()=>selectedLengthMm||optionLength()||500;
    const mode=()=>root.querySelector('[data-gw-mode]')?.value||'repeat';
    const dimensions=()=>({width:305,length:lengthFromVariant()});
    maker.giftWrapMetadata=()=>({
      widthMm:305,lengthMm:lengthFromVariant(),priceMinor:Number(maker.variant?.price||Math.round(priceForLength(lengthFromVariant())*100)),
      currency:maker.product.currency||'GBP',variantId:maker.variant?.id||null,variantTitle:maker.variant?.title||'',
      quantity:maker.currentDisplayQuantity?.()||1,tileCount:maker.state.length,activeTile:maker.surface,
      tileSequence:[...tileSequence],sheetBackground,
      pattern:{mode:mode(),tileMm:Number(root.querySelector('[data-gw-cell]')?.value)||200,
      gapMm:Number(root.querySelector('[data-gw-gap]')?.value)||0,offsetXMm:Number(root.querySelector('[data-gw-x]')?.value)||0,
      offsetYMm:Number(root.querySelector('[data-gw-y]')?.value)||0}
    });
    const drawFullSheet=async({production=false,index=0}={})=>{
      const priorPreset=maker.preset,priorExport=maker.renderingExport;
      const length=lengthFromVariant(),width=305,dpi=production?300:96;
      const out=document.createElement('canvas');
      const px=production?sheetPixelDimensions(length,dpi,width):{width:305,height:length};
      out.width=px.width;out.height=px.height;
      const baseRender=maker._opsGiftWrapBaseRender,tiles=[];
      maker.renderingExport=true;
      try{
        maker.preset={...priorPreset,h:priorPreset.w};
        for(let tileIndex=0;tileIndex<maker.state.length;tileIndex++){
          tiles.push(await baseRender(tileIndex,{dpi,preview:!production}));
        }
        const cellMm=Number(root.querySelector('[data-gw-cell]')?.value)||200;
        const gapMm=Number(root.querySelector('[data-gw-gap]')?.value)||0,scale=out.width/width;
        drawPattern(out.getContext('2d'),tiles,out.width,out.height,{
          mode:mode(),sequence:tileSequence,cellPx:Math.max(1,Math.round(cellMm*scale)),gapPx:Math.max(0,Math.round(gapMm*scale)),
          offsetXPx:Math.round((Number(root.querySelector('[data-gw-x]')?.value)||0)*scale),
          offsetYPx:Math.round((Number(root.querySelector('[data-gw-y]')?.value)||0)*scale),background:sheetBackground
        });
      }finally{maker.preset=priorPreset;maker.renderingExport=priorExport}
      return out;
    };
    const originalDraw=maker.draw.bind(maker),originalRender=maker.renderSurface.bind(maker);
    maker._opsGiftWrapBaseRender=originalRender;
    maker.renderSurface=async function(index,opts={}){return drawFullSheet({production:!opts.preview,index:0})};
    maker.requiredSurfaceIndexes=()=>[0];
    maker.updateSurfaceAvailability=()=>{};
    maker.surfaceSlug=()=> 'gift-wrap-sheet';
    maker.surfaceIsBlank=()=>!maker.state.some(surface=>surface?.image||surface?.decorations?.length||(surface?.texts||[]).some(text=>String(text.text||'').trim()));
    let drawToken=0;
    const refresh=()=>{
      const token=++drawToken;
      requestAnimationFrame(async()=>{
        if(token!==drawToken)return;
        try{
          const preview=await drawFullSheet({production:false});
          if(token!==drawToken)return;
          canvas.width=preview.width;canvas.height=preview.height;
          ctx.clearRect(0,0,canvas.width,canvas.height);ctx.drawImage(preview,0,0);
          const d=dimensions(),label=root.querySelector('[data-gw-dimensions]');if(label)label.textContent=`${d.width} mm × ${(d.length/1000).toFixed(1).replace(/\.0$/,'')} m`;
          const hasArtwork=maker.state.some(current=>Boolean(current.image||current.decorations?.length||(current.texts||[]).some(t=>String(t.text||'').trim())));
          const warning=root.querySelector('[data-gw-warning]');if(warning)warning.hidden=!isMirror(mode())||!hasArtwork;
          const current=maker.current(),image=current.image||current.decorations?.find(item=>item.img)?.img,quality=root.querySelector('[data-gw-resolution]');
          if(quality&&image){const effective=Math.round(Math.min(image.naturalWidth||image.width,image.naturalHeight||image.height)/(cellMmForQuality()/25.4));quality.textContent=`Selected tile artwork: about ${effective} DPI at this tile size${effective<180?' — may look soft when printed.':''}`}
          else if(quality)quality.textContent='Upload a sharp original for the cleanest print.';
        }catch(error){const label=root.querySelector('[data-gw-dimensions]');if(label)label.textContent='Preview could not be updated. Please try again.';console.error(error)}
      });
    };
    function cellMmForQuality(){return Number(root.querySelector('[data-gw-cell]')?.value)||200}
    maker.draw=function(){originalDraw();if(!maker.renderingExport)refresh()};

    const range=document.createElement('label');range.className='ops-maker__field ops-gift-wrap__length';range.innerHTML=`<span>Sheet length <output data-gw-length-readout>0.5 m · £4.95</output></span><input type="range" min="500" max="2000" step="100" value="500" data-gw-length aria-label="Wrapping paper length">`;
    const option=productOption();
    const select=option?root.querySelector(`[data-option-index="${option.index}"]`):null;
    const optionLabel=select?.closest('label');if(optionLabel)optionLabel.classList.add('ops-gift-wrap__variant-select');
    const summary=root.querySelector('[data-summary]');if(summary)summary.before(range);else order.append(range);
    const setLength=mm=>{
      const length=clamp(Math.round(Number(mm)/100)*100,500,2000),slider=range.querySelector('[data-gw-length]'),out=range.querySelector('[data-gw-length-readout]');
      selectedLengthMm=length;slider.value=String(length);
      const optionNow=productOption(),selectNow=optionNow?root.querySelector(`[data-option-index="${optionNow.index}"]`):null;
      let target=null;
      if(selectNow){
        target=variantForLength(maker.product.variants,length,optionNow.index);
        const optionValue=target?.options?.[optionNow.index];
        const choice=[...selectNow.options].find(o=>o.value===optionValue||new RegExp(`(?:^|\\D)${length}\\s*mm(?:\\D|$)`,'i').test(o.value));
        if(choice&&selectNow.value!==choice.value){selectNow.value=choice.value;maker.updateVariant()}
      }
      out.textContent=`${(length/1000).toFixed(1).replace(/\.0$/,'')} m · ${priceLabel(target?.price??maker.variant?.price??Math.round(priceForLength(length)*100))}`;
      maker.draw();
    };
    const applySavedGiftWrap=details=>{
      if(!details||Number(details.widthMm)!==305)return;
      const p=details.pattern||{},modeSelect=controls.querySelector('[data-gw-mode]');
      if(modeSelect&&[...modeSelect.options].some(o=>o.value===p.mode))modeSelect.value=p.mode;
      const setControl=(selector,value,min,max,step)=>{
        const input=controls.querySelector(selector);if(!input||!Number.isFinite(Number(value)))return;
        const snapped=clamp(Math.round(Number(value)/step)*step,min,max);input.value=String(snapped);
      };
      setControl('[data-gw-cell]',p.tileMm,100,305,5);
      setControl('[data-gw-gap]',p.gapMm,0,40,1);
      setControl('[data-gw-x]',p.offsetXMm,-100,100,1);
      setControl('[data-gw-y]',p.offsetYMm,-100,100,1);
      controls.querySelector('[data-gw-cell-readout]').textContent=`${controls.querySelector('[data-gw-cell]').value} mm`;
      controls.querySelector('[data-gw-gap-readout]').textContent=`${controls.querySelector('[data-gw-gap]').value} mm`;
      sheetBackground=/^#[0-9a-f]{6}$/i.test(details.sheetBackground||'')?details.sheetBackground:'#ffffff';
      tileTools.querySelector('[data-gw-background]').value=sheetBackground;
      const savedOrder=Array.isArray(details.tileSequence)?details.tileSequence.map(Number).filter(index=>Number.isInteger(index)&&index>=0&&index<maker.state.length):[];
      tileSequence=savedOrder.length?savedOrder:[0];
      if(Number(details.lengthMm))setLength(details.lengthMm);
      renderTileControls();
      maker.draw();
    };
    const originalSerialiseSurface=maker.serialiseSurface.bind(maker),originalHydrateSurface=maker.hydrateSurface.bind(maker);
    maker.serialiseSurface=async function(surface,index){
      const saved=await originalSerialiseSurface(surface,index);
      saved.decorations=saved.decorations.map((item,position)=>{
        const source=surface.decorations[position]||{},copy={...item};
        if(source.artworkFile instanceof Blob){copy.artworkFile=source.artworkFile;copy.artworkName=source.artworkFile.name||source.name||'tile-artwork.png'}
        return copy;
      });
      return saved;
    };
    maker.hydrateSurface=async function(saved,index){
      const surface=await originalHydrateSurface(saved,index);
      for(const item of Array.isArray(saved?.decorations)?saved.decorations:[]){
        if(!(item.artworkFile instanceof Blob))continue;
        try{
          const img=await this.imageFromBlob(item.artworkFile,item.artworkName||'tile-artwork.png');
          surface.decorations=surface.decorations.filter(existing=>existing.id!==item.id);
          surface.decorations.push({...item,img,artworkFile:item.artworkFile,name:item.name||'Uploaded artwork'});
        }catch(error){console.warn('A saved tile artwork could not be restored.',error)}
      }
      return surface;
    };
    const refreshArtControls=()=>{
      const decoration=maker.selectedDecoration(),box=tileTools.querySelector('[data-gw-art-controls]');
      box.hidden=!decoration;
      if(decoration)tileTools.querySelector('[data-gw-art-scale]').value=String(decoration.scale||1);
    };
    const originalWriteControls=maker.writeControls.bind(maker);
    maker.writeControls=function(){originalWriteControls();refreshArtControls()};
    const originalRestoreProject=maker.restoreProject.bind(maker);
    maker.restoreProject=async function(record){
      await originalRestoreProject(record);
      if(this.builderId!=='gift-wrap'||!record?.giftWrap)return;
      const count=clamp(Number(record.giftWrap.tileCount)||record.surfaces?.length||1,1,MAX_TILES);
      this.state=[];
      for(let index=0;index<count;index++)this.state.push(await this.hydrateSurface(record.surfaces?.[index]||{},index));
      this.surface=clamp(Number(record.giftWrap.activeTile)||0,0,this.state.length-1);
      applySavedGiftWrap(record.giftWrap);
      this.writeControls();renderTileControls();this.draw();
    };
    range.querySelector('[data-gw-length]').addEventListener('input',e=>setLength(e.target.value));
    select?.addEventListener('change',()=>requestAnimationFrame(()=>setLength(optionLength()||selectedLengthMm)));
    for(const input of controls.querySelectorAll('select,input'))input.addEventListener('input',()=>{const cell=controls.querySelector('[data-gw-cell]'),gap=controls.querySelector('[data-gw-gap]');controls.querySelector('[data-gw-cell-readout]').textContent=`${cell.value} mm`;controls.querySelector('[data-gw-gap-readout]').textContent=`${gap.value} mm`;maker.draw()});
    controls.querySelector('[data-gw-mode]').addEventListener('change',()=>maker.draw());
    tileSelector.addEventListener('click',event=>{
      const button=event.target.closest('[data-gw-tile]');
      if(button)activateTile(Number(button.dataset.gwTile));
    });
    tileTools.querySelector('[data-gw-add-tile]').addEventListener('click',()=>{
      if(maker.state.length>=MAX_TILES)return maker.status(`You can use up to ${MAX_TILES} tiles in one repeat.`,'error');
      const wasSimple=tileSequence.length===maker.state.length&&tileSequence.every((tile,index)=>tile===index);
      const index=maker.state.length;maker.state.push(maker.emptySurface());
      if(wasSimple)tileSequence.push(index);
      activateTile(index);
      maker.status(`Tile ${index+1} added. Upload artwork or add text to design it.`,'success');
    });
    tileTools.querySelector('[data-gw-append-tile]').addEventListener('click',()=>{
      tileSequence.push(maker.surface);renderTileControls();maker.draw();
    });
    tileTools.querySelector('[data-gw-reset-sequence]').addEventListener('click',()=>{
      tileSequence=[0];renderTileControls();maker.draw();
    });
    sequenceList.addEventListener('click',event=>{
      const button=event.target.closest('[data-gw-remove-sequence]');
      if(!button||tileSequence.length<=1)return;
      tileSequence.splice(Number(button.dataset.gwRemoveSequence),1);renderTileControls();maker.draw();
    });
    tileTools.querySelector('[data-gw-background]').addEventListener('input',event=>{
      sheetBackground=event.target.value;maker.draw();
    });
    const extraUpload=tileTools.querySelector('[data-gw-extra-upload]');
    tileTools.querySelector('[data-gw-extra-upload-button]').addEventListener('click',()=>extraUpload.click());
    tileTools.querySelector('[data-gw-extra-library]').addEventListener('click',()=>{
      if(typeof window.OPSArtworkSource?.open==='function')window.OPSArtworkSource.open(extraUpload);
      else maker.status('My Artwork is unavailable. Use Upload artwork instead.','error');
    });
    extraUpload.addEventListener('change',async event=>{
      const files=[...(event.target.files||[])];event.target.value='';
      for(const file of files){
        try{
          const img=await maker.imageFromBlob(file,file.name),surface=maker.current(),position=surface.decorations.length;
          const offset=(position%3-1)*Math.min(170,maker.canvas.width*.16);
          const item={id:`art-${Date.now().toString(36)}-${position}`,name:'Uploaded artwork',img,artworkFile:file,x:offset,y:Math.floor(position/3)*70,scale:.65,rotation:0};
          surface.decorations.push(item);surface.selectedDecorationId=item.id;surface.selectedTextId=null;
        }catch(error){maker.status(`${file.name} could not be added. Choose a PNG, JPG, WebP or SVG image.`,'error')}
      }
      maker.writeControls();maker.draw();
    });
    tileTools.querySelector('[data-gw-art-scale]').addEventListener('input',event=>{
      const decoration=maker.selectedDecoration();if(decoration){decoration.scale=Number(event.target.value)||1;maker.draw()}
    });
    tileTools.querySelector('[data-gw-delete-art]').addEventListener('click',()=>{
      maker.deleteSelectedDecoration();maker.writeControls();maker.draw();
    });
    renderTileControls();
    full.querySelector('[data-gw-open-sheet-preview]').addEventListener('click',()=>openPreview('sheet'));
    stage.addEventListener('click',event=>{
      if(!event.target.closest('canvas'))return;
      if(stage.dataset.gwMoved==='true'){stage.dataset.gwMoved='false';return}
      openPreview('tile');
    });
    full.querySelector('[data-gw-sheet]').addEventListener('click',()=>openPreview('sheet'));
    let pointerStart=null;
    stage.addEventListener('pointerdown',event=>{stage.dataset.gwMoved='false';pointerStart={x:event.clientX,y:event.clientY}});
    window.addEventListener('pointermove',event=>{
      if(!pointerStart)return;
      if(Math.hypot(event.clientX-pointerStart.x,event.clientY-pointerStart.y)>6)stage.dataset.gwMoved='true';
    },{passive:true});
    window.addEventListener('pointerup',()=>{pointerStart=null});
    previewDialog.querySelector('[data-gw-close-preview]').addEventListener('click',()=>previewDialog.close());
    const lengthSlider=range.querySelector('[data-gw-length]');lengthSlider.value=String(lengthFromVariant());range.querySelector('[data-gw-length-readout]').textContent=`${(lengthFromVariant()/1000).toFixed(1).replace(/\.0$/,'')} m · ${priceLabel(maker.variant?.price??Math.round(priceForLength(lengthFromVariant())*100))}`;
    const originalAddSavedDesignToCart=maker.addSavedDesignToCart.bind(maker);
    maker.addSavedDesignToCart=function(props){return originalAddSavedDesignToCart({...props,...cartPropertiesFor(this.giftWrapMetadata())})};
    originalDraw();refresh();
  }

  function boot(){if(typeof document!=='undefined')document.querySelectorAll(SELECTOR).forEach(initialize)}
  if(typeof document!=='undefined'){
    if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',boot,{once:true});else boot();
    document.addEventListener('shopify:section:load',boot);
  }
  if(typeof module==='object'&&module.exports)module.exports={getLayout,drawPattern,priceForLength,sheetPixelDimensions,cartPropertiesFor,variantForLength};
})();
